import { LabFeatureFlags } from 'constants/lab'
import { useLabFeatureFlag } from 'hooks/FeatureFlag/useLabFeatureFlag'
import useIsSearchPage from 'hooks/Navigation/useIsSearchPage'
import useIsHomePage from 'hooks/Navigation/useIsHomePage'
import { useMemo } from 'react'
import SiteTakeoverPopup from 'takeovers/utils/components/SiteTakeoverPopup'
import { useShouldShowBanner } from 'takeovers/utils/hooks/useShouldShowBanner'
import { isLabTakeoverBannerContentPayload } from 'lib/lab/dynamicPayload'
import useAgentHubMaldivesEventModal from 'agentHub/hooks/useAgentHubMaldivesEventModal'
import useAgentHubSocieteLoginPrompt from 'agentHub/hooks/useAgentHubSocieteLoginPrompt'
import PageBannerCarousel from 'LuxKit/Banners/PageBannerCarousel'
import type { PageBannerCarouselItem } from 'LuxKit/Banners/types'
import useNotificationBannerItem from './HeaderBanners/useNotificationBannerItem'
import useLuxPlusWarningItem from 'luxPlus/components/Banners/useLuxPlusWarningItem'
import useLuxPlusRenewalBannerItem from 'luxPlus/components/Banners/useLuxPlusRenewalBannerItem'
import useLoyaltyActivationBannerItem from 'loyalty/components/Banners/useLoyaltyActivationBannerItem'
import useAtolBannerItem from './HeaderBanners/useAtolBannerItem'
import useAgentHubBannerItems from './HeaderBanners/useAgentHubBannerItems'
import useCookiesBannerItem from './HeaderBanners/useCookiesBannerItem'
import useSiteTakeoverBannerItem from 'takeovers/utils/components/useSiteTakeoverBannerItem'
import useLuxPlusFlightVoucherOfferBannerItem from 'luxPlus/components/Banners/useLuxPlusFlightVoucherOfferBannerItem'

/**
 * Contains all the banners that are meant to be displayed on top of the page.
 */
function HeaderTopBanners() {
  const notificationItem = useNotificationBannerItem()
  const warningItem = useLuxPlusWarningItem()
  const renewalItem = useLuxPlusRenewalBannerItem()
  const loyaltyItem = useLoyaltyActivationBannerItem()
  const atolItem = useAtolBannerItem()
  const agentHubItems = useAgentHubBannerItems()
  const cookiesItem = useCookiesBannerItem()
  const takeoverItem = useSiteTakeoverBannerItem()
  const flightVoucherItem = useLuxPlusFlightVoucherOfferBannerItem()

  const carouselItems = useMemo(() => [
    notificationItem,
    warningItem,
    renewalItem,
    loyaltyItem,
    atolItem,
    ...agentHubItems,
    cookiesItem,
    takeoverItem,
    flightVoucherItem,
  ].filter((item): item is PageBannerCarouselItem => !!item), [
    notificationItem,
    warningItem,
    renewalItem,
    loyaltyItem,
    atolItem,
    agentHubItems,
    cookiesItem,
    takeoverItem,
    flightVoucherItem,
  ])

  useAgentHubMaldivesEventModal()

  const enableTakeoverBannerContent = useLabFeatureFlag(LabFeatureFlags.enableTakeoverBannerContent)
  const isSearchPage = useIsSearchPage()
  const isHomePage = useIsHomePage()
  const shouldShowGenericBanner = useShouldShowBanner({ location: 'generic' })

  const bannerContentPayload: App.LabTakeoverBannerContentPayload | undefined = useMemo(() => {
    if (enableTakeoverBannerContent?.enabled) {
      const payload = enableTakeoverBannerContent.value
      if (isLabTakeoverBannerContentPayload(payload)) {
        return payload
      }
    }
    return undefined
  }, [enableTakeoverBannerContent])

  useAgentHubSocieteLoginPrompt()

  const showPopup = !isHomePage && !isSearchPage && shouldShowGenericBanner &&
    (bannerContentPayload?.genericBannerType === 'takeoverpopup' || bannerContentPayload?.genericBannerType === 'whitepopup')

  return <div className="no-print">
    {carouselItems.length > 0 && <PageBannerCarousel items={carouselItems} />}
    {showPopup && <SiteTakeoverPopup />}
  </div>
}

export default HeaderTopBanners
